import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowRight } from "lucide-react";
import { IMG } from "@/data/content";
import { MaskedLines } from "@/components/wf/primitives";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section id="hero" ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMG.hero} alt="Aerial view of the Pacific ocean and scattered atolls" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-brand-navy" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/20 to-brand-navy/60" aria-hidden="true" />

      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-16 lg:pb-24">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="flex items-center gap-3 mb-8">
          <span className="h-px w-10 bg-brand-aqua" />
          <span className="font-sans text-xs tracking-[0.28em] uppercase text-brand-aqua">Events · Accommodation · Tours · Logistics across the moana</span>
        </motion.div>

        <MaskedLines
          testid="hero-headline"
          lines={["We know the way", "across the moana."]}
          delay={0.4}
          className="font-serif text-[clamp(2.75rem,8vw,7rem)] leading-[0.95] tracking-tight text-brand-sand"
        />

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 max-w-2xl font-sans text-base sm:text-lg text-brand-sand/80 leading-relaxed">
          One trusted local operator across Tuvalu and the wider Pacific — planning and running events,
          arranging accommodation, and designing and guiding tours. We carry the whole coordination burden, so you don't.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4">
          <button onClick={() => scrollTo("contact")} data-testid="hero-plan-cta"
            className="group inline-flex items-center gap-2 px-7 py-4 bg-brand-coral text-brand-navy font-medium hover:bg-brand-sand transition-colors">
            Plan with us <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <button onClick={() => scrollTo("services")} data-testid="hero-services-cta"
            className="group inline-flex items-center gap-2 px-7 py-4 border border-brand-teal text-brand-teal hover:bg-brand-teal hover:text-brand-navy transition-colors">
            What we do <ArrowDownRight className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};
